import { DateTime } from 'luxon';

import type {
  AggregateBy,
  CategoryData,
  CategoryPeriod,
  CategoryPeriodValue,
  ScorecardItem,
} from '../types';

export {
  useDateRangeParam,
  useScorecardLinkBuilder,
  useSelectedCategoriesParam,
} from './scorecardUrlState';
export * from './notApplicableInfo';

export const toStandardISO = (date: DateTime): string => {
  return `${
    date
      .startOf('second')
      .toISO({ includeOffset: false, suppressMilliseconds: true }) as string
  }Z`;
};

export const CHART_COLORS_LIGHT = [
  '#883eb1',
  '#b1623e',
  '#a73b37',
  '#c7cf32',
  '#1a4975',
  '#2e7d32',
  '#f57c00',
  '#d32f2f',
  '#7b1fa2',
  '#0288d1',
];

export const CHART_COLORS_DARK = [
  '#ce93d8',
  '#ffcc80',
  '#ef9a9a',
  '#e6ee9c',
  '#64b5f6',
  '#81c784',
  '#ffcc02',
  '#e57373',
  '#ba68c8',
  '#4fc3f7',
];

function* colorGenerator(palette: string[]) {
  let index = 0;
  while (true) {
    yield palette[index % palette.length];
    index++;
  }
}

const colorGen = colorGenerator(CHART_COLORS_LIGHT);

export const getRandomColor = (): string => colorGen.next().value as string;

type CheckStatus = 'passed' | 'failed' | 'N/A';

type RepoCheck = {
  check_status: CheckStatus;
  team_id?: string;
  value?: number;
  [key: string]: any;
};

type Repository = {
  repository_name: string;
  checks: Record<string, RepoCheck>;
};

type TeamNode = {
  team_id: string;
  repositories?: Repository[];
  children: TeamNode[];
  checks?: { check_status: string; [key: string]: any }[];
};

type CheckStatusSummary = {
  checkName: string;
  passed: boolean;
  label: string;
  description: string;
  checkDetails: { check_status: string; [key: string]: any }[];
};

export const getTeamBasedChecksStatus = (
  teamNode: TeamNode,
  check_structure: any,
): CheckStatusSummary[] => {
  if (!teamNode) return [];

  const checkNames = Object.keys(check_structure);

  const collectAllTeams = (node: TeamNode): TeamNode[] => {
    let teams: TeamNode[] = [node];
    for (const child of node.children || []) {
      teams = teams.concat(collectAllTeams(child));
    }
    return teams;
  };

  const allTeams = collectAllTeams(teamNode);

  return checkNames.map(checkName => {
    const matchesCheck = (check: any) =>
      check !== null &&
      check !== undefined &&
      (check.check_name
        ? check.check_name === checkName
        : checkNames.length === 1);

    const statuses = allTeams
      .map(team =>
        (team.checks as any[] | undefined)
          ?.filter(matchesCheck)
          .map((check: any) => check?.check_status),
      )
      .flat()
      .filter((status): status is string => status !== undefined);

    const passed =
      statuses.length > 0 && !statuses.some(status => status === 'failed');

    const currentCheckStructure =
      check_structure[checkName as keyof typeof check_structure];

    const checkDetails = allTeams
      .filter(team =>
        (team.checks as any[] | undefined)?.some(
          (check: any) =>
            check?.check_status === 'failed' && matchesCheck(check),
        ),
      )
      .map(team => ({
        check_status: 'failed',
        team_name: team.team_id,
        failing_resources:
          (team.checks as any[] | undefined)
            ?.filter(
              (check: any) =>
                check?.check_status === 'failed' && matchesCheck(check),
            )
            .map((check: any) => ({ ...check, team_name: team.team_id })) ?? [],
      }))
      .flatMap(cd => cd.failing_resources);

    return {
      checkName,
      passed,
      label: currentCheckStructure.display_name,
      description: currentCheckStructure.description,
      checkDetails,
    };
  });
};

export const getRepoBasedChecksStatus = (
  teamNode: TeamNode,
  check_structure: any,
): CheckStatusSummary[] => {
  const checkNames = Object.keys(check_structure);

  const collectAllRepos = (
    node: TeamNode,
  ): (Repository & { team_id: string })[] => {
    let repos = (node.repositories || []).map(r => ({
      ...r,
      team_id: node.team_id,
    }));
    for (const child of node.children || []) {
      repos = repos.concat(collectAllRepos(child));
    }
    return repos;
  };

  const allRepos = collectAllRepos(teamNode);

  return checkNames.map(checkName => {
    const statuses = allRepos
      .map(repo => repo.checks?.[checkName]?.check_status)
      .filter((status): status is CheckStatus => status !== undefined);

    const allPassedOrNA =
      statuses.length > 0 &&
      statuses.every(status => status === 'passed' || status === 'N/A');

    const currentCheckStructure =
      check_structure[checkName as keyof typeof check_structure];

    const checkDetails = allRepos
      .filter(repo => repo.checks?.[checkName]?.check_status === 'failed')
      .map(repo => ({
        ...repo.checks[checkName],
        check_status: 'failed' as const,
        repository_name: repo.repository_name,
        team_id: repo.team_id,
        check_name: checkName,
      }));

    return {
      checkName,
      passed: allPassedOrNA,
      label: currentCheckStructure.display_name,
      description: currentCheckStructure.description,
      checkDetails,
    };
  });
};

export const getScorecardItems = (
  categoriesData: CategoryData[],
  aggregation?: AggregateBy,
  isDark?: boolean,
): ScorecardItem[] | null => {
  if (
    !categoriesData?.length ||
    !categoriesData.every(categoryData => !!categoryData?.data?.periods)
  ) {
    return null;
  }
  const palette = isDark ? CHART_COLORS_DARK : CHART_COLORS_LIGHT;
  const localGen = colorGenerator(palette);

  const getFormattedDate = (isoDate: string) => {
    if (aggregation === 'week') {
      const date = DateTime.fromISO(isoDate);
      const { weekNumber, year } = date;
      return `W${weekNumber}-${year} (${DateTime.fromISO(
        isoDate,
      ).toLocaleString({
        month: 'short',
        day: 'numeric',
      })})`;
    } else if (aggregation === 'month') {
      return DateTime.fromISO(isoDate).toLocaleString({
        month: 'long',
        year: 'numeric',
      });
    } else if (aggregation === 'quarter') {
      const date = DateTime.fromISO(isoDate);
      return `${date.year}-Q${date.quarter} (${date.toLocaleString({
        month: 'short',
      })})`;
    }
    return DateTime.fromISO(isoDate).toLocaleString({
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return categoriesData.map((categoryData: CategoryData) => ({
    checked: true,
    color: localGen.next().value as string,
    label: categoryData.category.trim(),
    valueOverTime: categoryData.data.periods.map((period: CategoryPeriod) => {
      const raw = period.value.aggregated_category_score_percentage;
      const value =
        raw === -1 ? null : Math.round(Math.max(raw, 0) * 100 * 100) / 100;
      return { date: getFormattedDate(period.start), value };
    }),
  }));
};

const denominatorDividendDifference = (value: CategoryPeriodValue) =>
  value.aggregated_category_score_proportion_denominator === -1
    ? -1
    : value.aggregated_category_score_proportion_denominator -
      value.aggregated_category_score_proportion_dividend;

const aggregatedValue = (value: CategoryPeriodValue) => {
  if (value.aggregated_category_score_percentage === -1) {
    return value.aggregated_category_score_percentage;
  }
  const percentage =
    Math.max(value.aggregated_category_score_percentage, 0) * 100;
  return `${Math.round(percentage * 100) / 100}%`;
};

const dummyValue = () => -1;

const KPIValueTransform: Record<string, (v: CategoryPeriodValue) => any> = {
  Velocity: dummyValue,
  'End of Life': aggregatedValue,
  'Privileged Identity Management': aggregatedValue,
  'Key Rotation': aggregatedValue,
  'Agent Coverage': aggregatedValue,
  'Public IP': denominatorDividendDifference,
  'Criticality Set': denominatorDividendDifference,
  'Blackduck Coverage': aggregatedValue,
  'Veracode Coverage': aggregatedValue,
  'Coverity Coverage': aggregatedValue,
  'Trivy Coverage': aggregatedValue,
  'Repository Ownership': dummyValue,
  'Investment Category': aggregatedValue,
};

export const getKPIItems = (
  categoriesData: CategoryData[],
): ScorecardItem[] | null => {
  if (
    !categoriesData?.length ||
    !categoriesData.every(categoryData => !!categoryData?.data?.periods)
  ) {
    return null;
  }

  return categoriesData.map((categoryData: CategoryData) => ({
    checked: true,
    color: getRandomColor(),
    label: categoryData.category.trim(),
    valueOverTime: categoryData.data.periods.map((period: CategoryPeriod) => ({
      date: DateTime.fromISO(period.start).toLocaleString({
        month: 'short',
        day: 'numeric',
      }),
      value: (KPIValueTransform[categoryData.category] ?? aggregatedValue)(
        period.value,
      ),
    })),
  }));
};
